import type { IPublicClientApplication } from '@azure/msal-browser';
import type { Tag, Workflow, WorkflowCreate, WorkflowUpdate, WorkflowsResponse, DocumentInstance, DocumentInstanceCreate, DocumentInstancesResponse, DocumentSummaryCreate, DocumentSummary } from '@/types/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

// This will be set by the AuthProvider
let msalInstance: IPublicClientApplication | null = null;

export const setMsalInstance = (instance: IPublicClientApplication) => {
  msalInstance = instance;
};

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
  if (!msalInstance) {
    throw new Error('MSAL instance not initialized');
  }

  // Get the current account
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No user account found');
  }

  try {
    // Get access token silently
    const response = await msalInstance.acquireTokenSilent({
      scopes: [`api://${import.meta.env.VITE_SSO_BACKEND_APP_ID}/user_impersonation`],
      account: accounts[0],
    });

    // Add authorization header
    const headers = {
      'Authorization': `Bearer ${response.accessToken}`,
      'Content-Type': 'application/json',
      ...options.headers,
    };

    return fetch(url, {
      ...options,
      headers,
    });
  } catch (error) {
    console.error('Failed to acquire token:', error);
    throw new Error('Failed to get access token');
  }
};

export const tagsApi = {
  async getTags(search?: string): Promise<Tag[]> {
    const url = new URL(`${API_BASE_URL}/tags/`);
    if (search) {
      url.searchParams.append('search', search);
    }
    
    const response = await makeAuthenticatedRequest(url.toString());
    if (!response.ok) {
      throw new Error(`Failed to fetch tags: ${response.statusText}`);
    }
    
    return response.json();
  }
};

export const workflowsApi = {
  async createWorkflow(workflowData: WorkflowCreate): Promise<Workflow> {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/workflows/`, {
      method: 'POST',
      body: JSON.stringify(workflowData),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create workflow: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getWorkflows(page: number = 1, size: number = 10, search?: string, tag?: string): Promise<WorkflowsResponse> {
    const url = new URL(`${API_BASE_URL}/workflows/`);
    url.searchParams.append('page', page.toString());
    url.searchParams.append('size', size.toString());
    if (search) {
      url.searchParams.append('search', search);
    }
    if (tag) {
      url.searchParams.append('tag', tag);
    }
    
    const response = await makeAuthenticatedRequest(url.toString());
    if (!response.ok) {
      throw new Error(`Failed to fetch workflows: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getWorkflow(id: number): Promise<Workflow> {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/workflows/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch workflow: ${response.statusText}`);
    }
    
    return response.json();
  },

  async updateWorkflow(id: number, workflowData: WorkflowUpdate): Promise<Workflow> {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/workflows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(workflowData),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update workflow: ${response.statusText}`);
    }
    
    return response.json();
  },

  async deleteWorkflow(id: number): Promise<void> {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/workflows/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete workflow: ${response.statusText}`);
    }
  }
};

export const documentsApi = {
  async createDocument(documentData: DocumentInstanceCreate): Promise<DocumentInstance> {
    // Create FormData for multipart/form-data
    const formData = new FormData();
    formData.append('file', documentData.file);
    formData.append('metadata_json', JSON.stringify(documentData.metadata));

    // Get auth token for multipart request
    if (!msalInstance) {
      throw new Error('MSAL instance not initialized');
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No user account found');
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: [`api://${import.meta.env.VITE_SSO_BACKEND_APP_ID}/user_impersonation`],
        account: accounts[0],
      });

      const headers = {
        'Authorization': `Bearer ${response.accessToken}`,
      };

      const fetchResponse = await fetch(`${API_BASE_URL}/documents/`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!fetchResponse.ok) {
        throw new Error(`Failed to create document: ${fetchResponse.statusText}`);
      }

      return fetchResponse.json();
    } catch (error) {
      console.error('Failed to acquire token:', error);
      throw new Error('Failed to get access token');
    }
  },

  async getDocuments(page: number = 1, size: number = 10, search?: string): Promise<DocumentInstancesResponse> {
    const url = new URL(`${API_BASE_URL}/documents/`);
    url.searchParams.append('page', page.toString());
    url.searchParams.append('size', size.toString());
    if (search) {
      url.searchParams.append('search', search);
    }
    
    const response = await makeAuthenticatedRequest(url.toString());
    if (!response.ok) {
      throw new Error(`Failed to fetch documents: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getDocument(id: number): Promise<DocumentInstance> {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/documents/${id}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch document: ${response.statusText}`);
    }
    
    return response.json();
  },

  async updateDocument(id: number, documentData: Partial<DocumentInstanceCreate>): Promise<DocumentInstance> {
    // Create FormData for multipart/form-data
    const formData = new FormData();
    if (documentData.file) {
      formData.append('file', documentData.file);
    }
    if (documentData.metadata) {
      formData.append('metadata', JSON.stringify(documentData.metadata));
    }

    // Get auth token for multipart request
    if (!msalInstance) {
      throw new Error('MSAL instance not initialized');
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No user account found');
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        scopes: [`api://${import.meta.env.VITE_SSO_BACKEND_APP_ID}/user_impersonation`],
        account: accounts[0],
      });

      const headers = {
        'Authorization': `Bearer ${response.accessToken}`,
      };

      const fetchResponse = await fetch(`${API_BASE_URL}/documents/${id}`, {
        method: 'PUT',
        headers,
        body: formData,
      });

      if (!fetchResponse.ok) {
        throw new Error(`Failed to update document: ${fetchResponse.statusText}`);
      }

      return fetchResponse.json();
    } catch (error) {
      console.error('Failed to acquire token:', error);
      throw new Error('Failed to get access token');
    }
  },

  async deleteDocument(id: number): Promise<void> {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/documents/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete document: ${response.statusText}`);
    }
  },
  async createSummary(documentSummary: DocumentSummaryCreate): Promise<DocumentSummary> {
    // Using makeAuthenticatedRequest handles the token and Content-Type: application/json
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/documents/summaries`, {
      method: 'POST',
      body: JSON.stringify(documentSummary),
    });

    if (!response.ok) {
      throw new Error(`Failed to create summary: ${response.statusText}`);
    }

    return response.json();
  }
};

export interface MetadataOption {
  id: string;
  name: string;
}

export const metadataApi = {
  async getProFormaOptions(): Promise<MetadataOption[]> {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/metadata/pro-forma`);
    if (!response.ok) {
      throw new Error(`Failed to fetch pro forma options: ${response.statusText}`);
    }
    return response.json();
  },

  async getDocumentTypeOptions(): Promise<MetadataOption[]> {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/metadata/document-type`);
    if (!response.ok) {
      throw new Error(`Failed to fetch document type options: ${response.statusText}`);
    }
    return response.json();
  },

  async getTerritoryOptions(): Promise<MetadataOption[]> {
    const response = await makeAuthenticatedRequest(`${API_BASE_URL}/metadata/territory`);
    if (!response.ok) {
      throw new Error(`Failed to fetch territory options: ${response.statusText}`);
    }
    return response.json();
  }
};
